import {
  ComboBox,
  IComboBox,
  IComboBoxOption,
  SelectableOptionMenuItemType,
  TextField,
} from '@fluentui/react';
import * as React from 'react';
import { isGetHelpSourceApp } from '../../../../../web/utils/statelessUtil';
import { getString } from '../../../../../web/utils/stringLoader';
import { RegExpPatterns, SourceApps } from '../../../../models/constants/constants';
import { Region, RegionType } from '../../models/modalityModels';
import { FreelanceRequest } from '../../models/requestModels';
import {
  getFieldClassNames,
  getFieldStyles,
  getRequestDetailsClassNames,
  getTextFieldStyles,
  IRequestDetailsStyles,
  IRequestDetailsThemedProps,
} from '../../styles/requestDetailsStyles';
import { IStyledComponent } from '../../styles/themesUI';

interface IPhoneFieldComponentProps {
  isPhoneEditingMode: boolean;
  isPhoneNumberValid: boolean;
  isRequestDetailsEditMode: boolean;
  phoneFieldPlaceHolder: { region: Region; index: number };
  regionInfo: Array<Region>;
  twoDigitCountryCode: string;
  userPhone: string;
  userCountryDialingCode: string;
  userPhoneWithoutDialingCode: string;
  sourceApp: SourceApps;
  isNonAgave: boolean;
  isTeams: boolean;

  // dispatchers
  dispatchUpdateRequest: (properties: Partial<FreelanceRequest>) => void;
  dispatchUpdateIsPhoneNumberValid: (isPhoneNumberValid: boolean) => void;
  setIsInputAllowed?: (isInputAllowed: boolean) => void;
}

interface IPhoneFieldState {
  comboBoxOptions: IComboBoxOption[];
  isExpandClicked: boolean;
  isExpand: boolean;
  isInputAllowed: boolean;
  selectedCountryCode: string;
}

type IThemedPhoneFieldProps = IPhoneFieldComponentProps & IStyledComponent<IRequestDetailsStyles>;

export class PhoneField extends React.PureComponent<
  IThemedPhoneFieldProps,
  IPhoneFieldState
> {
  private optionListLimit = 10;
  private comboBoxRef: any;
  private comboBoxElementRef: React.RefObject<HTMLDivElement>;
  private initComboBoxOptions: IComboBoxOption[] = [];
  private fieldStyles = getFieldStyles({
    theme: this.props.theme,
    sourceApp: this.props.sourceApp,
    isNonAgave: this.props.isNonAgave,
    isTeams: this.props.isTeams
  } as IRequestDetailsThemedProps);
  private fieldClassNames = getFieldClassNames(getFieldStyles, {
    theme: this.props.theme,
    sourceApp: this.props.sourceApp,
  });
  private comboBoxValue: string;
  private requestDetailsClassNames = getRequestDetailsClassNames(
    this.props.styles,
    { theme: this.props.theme }
  );
  private isGetHelp: boolean;

  public constructor(props: IThemedPhoneFieldProps) {
    super(props);
    this.comboBoxRef = React.createRef<IComboBox>();
    this.comboBoxElementRef = React.createRef<HTMLDivElement>();
    this.state = {
      comboBoxOptions: this.initComboBoxOptions,
      isExpandClicked: false,
      isExpand: false,
      isInputAllowed: true,
      selectedCountryCode: '',
    };
    this.isGetHelp = isGetHelpSourceApp(props.sourceApp);
  }

  public componentDidMount = () => {
    // Display C2C Country Code PlaceHolder
    if (!!this.props.regionInfo) {
      this.setState({ comboBoxOptions: this.getInitialOptionList() });
      if (!!this.props.phoneFieldPlaceHolder && !!this.props.phoneFieldPlaceHolder.region) {
        this.props.dispatchUpdateRequest({
          UserPhoneDialingCodeIndex: this.props.phoneFieldPlaceHolder.index.toString(),
          TwoDigitCountryCode: this.props.phoneFieldPlaceHolder.region.TwoDigitCountryCode,
        });
      }
    }
  };

  public componentDidUpdate(
    prevProps: Readonly<IThemedPhoneFieldProps>
  ): void {
    if (this.props.regionInfo !== prevProps.regionInfo) {
      this.setState(() => {
        return { comboBoxOptions: this.getOnExpandOptionList() };
      });
    }
  }

  public render(): React.ReactNode {
    return this.props.isPhoneEditingMode ? (
      this.getEditModeComponents()
    ) : (
      <div className={this.requestDetailsClassNames.propertyLine}>
        <div className={this.requestDetailsClassNames.propertyLabel}>
          {getString('requestDetails_phoneNumberLabel')}
        </div>
        <div>{this.props.userPhone}</div>
      </div>
    );
  }

  private updateExpandedState(expanded: boolean): () => void {
    let self = this;
    let sExpanded = `${expanded}`;
    return () => self.comboBoxElementRef.current.setAttribute('aria-expanded', sExpanded);
  }

  private getEditModeComponents = () => {
    const textFieldStyles = getTextFieldStyles({
      theme: this.props.theme,
      isInputValid: this.props.isPhoneNumberValid,
      isPhoneOrEmailField: true,
      isTeams: this.props.isTeams,
    });
    if (!this.props.regionInfo) {
      return null;
    } else {
      return (
        <div className={this.fieldClassNames.fieldSection}>
          <div className={this.fieldClassNames.fieldFlexLine}>
            <div className={this.fieldClassNames.dialingCodeDropdown}>
              <div className={this.fieldClassNames.fieldLabel}>
                <span id='userCountryCodeLabel'>{getString('requestCreation_countryCodeLabel')}</span>
                {/** We show the red * in request creation for both non-sticky mode and
                * sticky mode. this.props.isRequestDetailsEditMode covers both of these 2 scenarios. */}
                {!this.props.isRequestDetailsEditMode && <span className={this.fieldClassNames.fieldLabelRequired}> *</span>}
              </div>
              <div>
                <ComboBox
                  id='userCountryCode'
                  role='combobox'
                  ariaLabel={getString('requestCreation_countryCodeLabel')}
                  aria-expanded={this.state.isExpand}
                  aria-controls='userCountryCode-list'
                  options={this.state.comboBoxOptions}
                  onChange={this.onUserCountryCodeDropdownChange}
                  onFocus={this.updateExpandedState(true)}
                  onBlur={this.updateExpandedState(false)}
                  onMenuDismissed={this.onMenuDismissed}
                  autoComplete='off'
                  useComboBoxAsMenuWidth
                  componentRef={this.comboBoxRef}
                  ref={this.comboBoxElementRef}
                  allowFreeform={true}
                  openOnKeyboardFocus={true}
                  onPendingValueChanged={this.onUserPendingValues}
                  placeholder={this.getPlaceHolderString()}
                  calloutProps={{
                    calloutMaxHeight: 420,
                  }}
                  styles={this.fieldStyles.dropDown}
                  comboBoxOptionStyles={this.fieldStyles.comboBoxOption}
                  iconButtonProps={{
                    styles: this.fieldStyles.iconButton,
                  }}
                  selectedKey={this.state.selectedCountryCode}
                  required={true}
                />
                <div
                  className={this.fieldClassNames.fieldInputInvalidMessage}
                  role='alert'
                >
                  {!this.state.isInputAllowed && getString('requestCreation_invalidCountryCodeText')}
                </div>
              </div>
            </div>
            <div className={this.fieldClassNames.fieldInputGroup}>
              <div className={this.fieldClassNames.fieldLabel}>
                <span>{getString('requestCreation_phoneNumberLabel')}</span>
                {!this.props.isRequestDetailsEditMode && <span className={this.fieldClassNames.fieldLabelRequired}> *</span>}
              </div>
              <div className={this.fieldClassNames.phoneNumberField}>
                <TextField
                  id='userPhoneWithoutDialingCodeInput'
                  type='tel'
                  value={this.props.userPhoneWithoutDialingCode}
                  onChange={this.onUserPhoneWithoutDialingCodeInputChange}
                  styles={textFieldStyles}
                  underlined={this.isGetHelp}
                  ariaLabel={getString('requestCreation_phoneNumberLabel')}
                />
                {!this.props.isPhoneNumberValid && (
                  <div
                    className={this.fieldClassNames.fieldInputInvalidMessage}
                    role='alert'
                    aria-live='assertive'
                  >
                    {getString('requestCreation_invalidPhoneNumberMessageText')}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      );
    }
  };

  private onUserCountryCodeDropdownChange = (
    event: React.FormEvent<IComboBox>,
    option: IComboBoxOption
  ) => {
    if (!!option && option.key === 'expand') {
      const newOptions = this.getOnExpandOptionList();
      this.setState({
        comboBoxOptions: newOptions,
        isExpandClicked: true,
        isExpand: true,
      });
      this.comboBoxRef.current.text = 'expandAll';
    } else if (!!option && option.key !== 'expand') {
      this.setState({ selectedCountryCode: String(option.key) });
      const data = option.data ? option.data.split('-') : [];
      this.props.dispatchUpdateRequest({
        UserPhoneDialingCodeIndex: option.index.toString(),
        UserCountryDialingCode: data[0] || '',
        TwoDigitCountryCode: data[1] || '',
      });
    }
  };

  private getInitialOptionList = (): IComboBoxOption[] => {
    const options: IComboBoxOption[] = [];

    // Add all other Top Results to Option List
    const arrTop = this.getTopResultsList(this.props.regionInfo);
    arrTop.forEach((ri) => {
      options.push({
        key: ri.index.toString(),
        data: `${ri.region.DialingCode}-${ri.region.TwoDigitCountryCode}`,
        index: ri.index,
        text: this.getCountryCodeTextDisplay(ri.region),
      });
    });

    // Add in divider
    options.push({
      key: '',
      text: '',
      itemType: SelectableOptionMenuItemType.Divider,
    });

    // Cut the List and only save first 10 options
    const listedOptions =
      options.length > this.optionListLimit
        ? options.slice(0, this.optionListLimit)
        : options;

    // Add expand button
    listedOptions.push({
      key: 'expand',
      data: '',
      index: -1,
      text: this.isGetHelp ? getString('ExpandToAllCountryAreaCodesDropdownItem') : 'Expand all Countries',
      styles: {
        optionText: {
          color: this.isGetHelp ? this.props.theme.palette.accent : '#337AB7',
          fontWeight: '500',
        },
      },
    });

    return listedOptions;
  };

  private getPlaceHolderString = (): string => {
    const placeHolderRegion = this.props.phoneFieldPlaceHolder;
    // Check if region information is displayed, if yes place the region; if not place the place holder.
    return placeHolderRegion.index > -1
      ? this.getCountryCodeTextDisplay(placeHolderRegion.region)
      : getString('requestCreation_dropDownSelectAnOptionText');
  };

  private getTopResultsList = (
    arr: Region[]
  ): { region: Region; index: number }[] => {
    const topResultsList: { region: Region; index: number }[] = [];
    arr.forEach((r: Region, idx: number) => {
      if (r.RegionType === RegionType.TopRegions) {
        topResultsList.push({ region: r, index: idx });
      }
    });
    return topResultsList;
  };

  private getCountryCodeTextDisplay = (region: Region): string => {
    return `(${region.DialingCode.slice(1)}) ${region.Name}`;
  };

  private onUserPhoneWithoutDialingCodeInputChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (!!event && !!event.target) {
      event.stopPropagation();
      this.props.dispatchUpdateRequest({
        UserPhoneWithoutDialingCode: event.target.value,
      });
      // we'll allow null values since we allow phone or email during ticket creation
      this.props.dispatchUpdateIsPhoneNumberValid(
        !event.target.value ||
        RegExpPatterns.PhoneNumber.test(event.target.value)
      );
    }
  };

  /**
   * there is a fluent ui bug onPendingValueChanged where callback is claled multiple times.
   * Keeping class properties to prevent multiple state updates: https://github.com/microsoft/fluentui/issues/20999
   */
  private onUserPendingValues = (
    option: IComboBoxOption,
    index: number,
    value: string
  ) => {
    // fabric seems to have a bug, adding this check so that we don't show false drop down error message when user selects the same value
    if (option === undefined && index === undefined && value === undefined) {
      return;
    }

    // Make the dropdown always open when users type to search
    if (!!value && value !== this.comboBoxValue) {
      this.comboBoxRef.current.focus(true);
    }

    // Set the isInputAllower with on users' typing
    if (!option || index === -1) {
      if (this.state.isInputAllowed) {
        const isInputAllowed = false;
        this.setState({
          isInputAllowed: isInputAllowed,
        });
        this.props.setIsInputAllowed && this.props.setIsInputAllowed(isInputAllowed);
      }
    } else if (!this.state.isInputAllowed) {
      const isInputAllowed = true;
      this.setState({
        isInputAllowed: isInputAllowed,
      });
      this.props.setIsInputAllowed && this.props.setIsInputAllowed(isInputAllowed);
    }

    // // // Update Option list with users' typing
    if (value === '' && this.comboBoxValue !== value && !option && !index) {
      this.comboBoxValue = value;
      if (this.state.isExpand) {
        this.setState(() => {
          return { comboBoxOptions: this.getOnExpandOptionList() };
        });
      } else {
        this.setState(() => {
          return { comboBoxOptions: this.getInitialOptionList() };
        });
      }
    } else if (!!value && value !== this.comboBoxValue && !option && !index) {
      this.comboBoxValue = value;
      const newOptions = this.getOnSearchOptionList(value);
      this.setState((prevState) => {
        return {
          comboBoxOptions: prevState.comboBoxOptions
            .splice(0, 0)
            .concat(newOptions),
        };
      });
    }
  };

  private getOnSearchOptionList = (value: string): IComboBoxOption[] => {
    const newOptions: IComboBoxOption[] = [];
    const originalOptions: IComboBoxOption[] = this.getSearchableOptionList();
    originalOptions.forEach((option: IComboBoxOption) => {
      if (
        !!option.text &&
        option.text.toLowerCase().indexOf(value.toLowerCase()) !== -1
      ) {
        newOptions.push(option);
      }
    });
    return newOptions;
  };

  private getOnExpandOptionList = (): IComboBoxOption[] => {
    return [
      {
        key: 'all',
        data: '',
        index: -1,
        text: this.isGetHelp ? getString('AllCountriesAreaCodeDropdownItem') : 'All Countries',
        itemType: SelectableOptionMenuItemType.Header,
      },
      ...this.getSearchableOptionList(),
    ];
  };

  private getSearchableOptionList = (): IComboBoxOption[] => {
    const optionList: IComboBoxOption[] = [];
    this.props.regionInfo.forEach((region: Region, index: number) => {
      optionList.push({
        key: index.toString(),
        data: `${region.DialingCode}-${region.TwoDigitCountryCode}`,
        index: index,
        text: this.getCountryCodeTextDisplay(region),
      });
      if (index === this.optionListLimit - 2) {
        optionList.push({
          key: '',
          data: '',
          index: -1,
          text: '',
          disabled: true,
          hidden: true,
        });
      }
    });
    return optionList;
  };

  private onMenuDismissed = (): void => {
    if (this.comboBoxRef.current.text === 'expandAll') {
      this.comboBoxRef.current.text = '';
      this.comboBoxRef.current.focus(true);
    }
  };
}
